import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { HeroBase, HeroCommonExtras, AnimatedHeadlineWords, cmsUrl } from './HeroBase';

// Mascot hero — cartoon mascot beside the copy with a coloured halo.
export const HeroMascot = ({ cms = {}, onPrimary, onOpenAvailability }) => {
  const v = cms.variants?.mascot || {};
  const onLeft = v.mascot_position === 'left';
  const halo = v.mascot_bg || '#FFF4D6';
  const badgeText  = cms.badge_text  || 'Creative Birthdays · Oakville';
  const primaryCta = cms.primary_cta_text   || 'Build Your Dream Party';
  const secondCta  = cms.secondary_cta_text || 'Check Availability';
  const extras = HeroCommonExtras({ cms });

  const copy = (
    <motion.div initial={{ opacity: 0, x: onLeft ? 20 : -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }} className="min-w-0">
      <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#FF3B6B] mb-3" data-testid="hero-badge">{badgeText}</span>
      <AnimatedHeadlineWords
        cms={cms}
        className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-5"
      />
      {extras.sub}
      {extras.location}
      {extras.pills({ when: 'above' })}
      <div className="flex flex-wrap gap-3">
        <Button onClick={onPrimary} size="lg" className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-7" data-testid="hero-primary-cta">
          <Calendar className="w-5 h-5 mr-2" /> {primaryCta}
        </Button>
        <Button onClick={onOpenAvailability} variant="outline" size="lg" className="rounded-full px-7" data-testid="hero-secondary-cta">
          <CalendarDays className="w-5 h-5 mr-2" /> {secondCta}
        </Button>
      </div>
      {extras.pills({ when: 'below' })}
      {extras.trustBadges}
    </motion.div>
  );

  const mascot = (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 110, damping: 12, delay: 0.05 }}
      className="relative flex justify-center"
    >
      <div className="relative w-56 h-56 sm:w-72 sm:h-72 rounded-full flex items-center justify-center shadow-xl" style={{ background: halo }} data-testid="hero-mascot">
        {v.mascot_image_url ? (
          <motion.img src={cmsUrl(v.mascot_image_url)} alt="Mascot" className="w-[80%] h-[80%] object-contain" whileHover={{ scale: 1.05, rotate: 3 }} transition={{ duration: 0.3 }} />
        ) : (
          <motion.span className="text-7xl sm:text-8xl" animate={{ y: [0, -8, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }} aria-hidden>🦄</motion.span>
        )}
      </div>
    </motion.div>
  );

  return (
    <HeroBase
      cms={cms}
      fallbackBg="linear-gradient(135deg,#FFFDF8 0%,#FFF8E7 50%,#FFEFF5 100%)"
      className="py-12 sm:py-20"
      testidVariant="mascot"
    >
      <div className="container mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center relative z-10">
        {onLeft ? <>{mascot}{copy}</> : <>{copy}{mascot}</>}
      </div>
    </HeroBase>
  );
};

export default HeroMascot;
